import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { galleryList } from "@/mocks/gallery";
import { specialList } from "@/mocks/special";

const principles = [
  {
    icon: "ri-archive-2-line",
    title: "按时间归档",
    text: "所有新闻按发布时间排列，不置顶、不做推荐排序干预，方便回溯任何一段时间里的公开露面。",
  },
  {
    icon: "ri-shield-check-line",
    title: "标注来源",
    text: "每条资讯都标注来源与类别，涉及未证实的传闻会单独归入「求证」类别，并在正文中说明。",
  },
  {
    icon: "ri-copyright-line",
    title: "尊重版权",
    text: "站内使用的图片与文字均为示范性内容，用于展示页面结构。正式上线前需替换为获得授权的内容。",
  },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于本站 - 张柏芝明星资讯平台介绍",
    description:
      "了解张柏芝明星资讯站的定位、内容范围、版权声明与联系方式。本站为非官方粉丝整理的资讯站点，欢迎通过反馈表单提供资料补充。",
    keywords: "张柏芝资讯站,关于我们,明星资讯平台,版权声明,意见反馈",
  });

  const stats = [
    { label: "新闻资讯", value: `${newsList.length}`, unit: "篇" },
    { label: "影视作品", value: `${worksList.length}`, unit: "部" },
    { label: "写真图片", value: `${galleryList.length}`, unit: "组" },
    { label: "特别专题", value: `${specialList.length}`, unit: "个" },
  ];

  return (
    <SiteLayout>
      <PageHero
        kicker="About Us"
        title="关于这个资讯站"
        description="我们做的事情很简单：把公开可见的报道、作品资料与图片按时间整理好，让关心张柏芝的人可以在一个地方看完。"
      >
        <Breadcrumb items={[{ label: "关于我们" }]} />
      </PageHero>

      <div className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <p className="font-heading text-2xl font-semibold text-primary-600 md:text-3xl">
                {item.value}
                <span className="ml-1 text-xs font-normal text-foreground-500">{item.unit}</span>
              </p>
              <p className="mt-1.5 text-[11px] text-foreground-600">{item.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_380px]">
          <div className="min-w-0">
            <section aria-label="网站介绍">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">网站介绍</h2>
              <div className="mt-4 space-y-4 text-[14px] leading-8 text-foreground-700">
                <p>
                  张柏芝明星资讯站是一个由粉丝自发整理的资讯平台，聚焦她的演艺事业与公开动态。
                  站点内容分为四个部分：新闻资讯、影视作品、写真图集与特别专题。
                </p>
                <p>
                  新闻通道按时间与类型归档，包含影视、生活、活动与求证四类；影视作品通道按年代与类型整理，
                  每部作品都有独立的角色介绍与剧照页面；写真图集把棚拍、活动现场与宣传照分开陈列；
                  专题则用于承载需要按线索顺序阅读的深度内容。
                </p>
                <p>
                  我们不设置评论区，也不做话题炒作。如果你发现资料有误，或者想补充某一段被遗漏的公开信息，
                  欢迎使用下方的反馈表单，我们会逐条整理。
                </p>
              </div>
            </section>

            <section aria-label="内容原则" className="mt-10">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">内容原则</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {principles.map((item) => (
                  <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-700">
                      <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-medium text-foreground-900">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-6 text-foreground-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section aria-label="版权声明" className="mt-10 rounded-lg bg-secondary-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-copyright-line text-primary-700" aria-hidden="true"></i>
                版权声明
              </h2>
              <div className="mt-3 space-y-2 text-xs leading-6 text-foreground-700">
                <p>
                  本站为非官方粉丝站点，与张柏芝本人及其经纪公司无任何隶属关系。站内文字、图片与影视资料为示范性内容，
                  仅用于展示页面结构与交互效果，不构成对任何事实的确认。
                </p>
                <p>
                  如需引用站内整理内容，请注明来源；若相关权利人认为内容存在版权问题，请通过反馈表单与我们联系，
                  我们会在核实后第一时间处理。
                </p>
                <p>© 2026 张柏芝明星资讯站 · 保留所有权利</p>
              </div>
            </section>

            <section aria-label="联系方式" className="mt-10">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">联系方式</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-mail-line text-primary-600" aria-hidden="true"></i>
                    邮箱
                  </p>
                  <p className="mt-2 text-sm text-foreground-900">hello@cecilia-archive.cn</p>
                  <p className="mt-1 text-[11px] text-foreground-500">资料补充与版权事宜优先使用邮件</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-time-line text-primary-600" aria-hidden="true"></i>
                    回复时间
                  </p>
                  <p className="mt-2 text-sm text-foreground-900">工作日 10:00 — 18:00</p>
                  <p className="mt-1 text-[11px] text-foreground-500">一般在一到两个工作日内回复</p>
                </div>
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            <FeedbackForm />
            <div className="rounded-lg bg-background-100 p-5">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">想先看看内容？</h3>
              <p className="mt-2 text-xs leading-6 text-foreground-600">这几个入口是最常被访问的页面。</p>
              <div className="mt-3 flex flex-col gap-2">
                {[
                  { label: "最新新闻动态", to: "/news" },
                  { label: "影视作品档案", to: "/works" },
                  { label: "年度写真合集", to: "/gallery" },
                  { label: "镜头里的二十年", to: "/special/s001" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="group flex items-center justify-between rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-800 transition-colors hover:text-primary-700"
                  >
                    {link.label}
                    <i className="ri-arrow-right-s-line text-foreground-400 transition-colors group-hover:text-primary-600" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}